import {cdnImage} from '@/util/cdnImage';
import {MUIContainer} from '@/components/MUI-Components';
import DisplayImage from '@/components/Ui-components/display-image';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import {memo} from 'react';
import {StaticImageData} from 'next/image';
const listCheckIcon = cdnImage('web3/Web3-Best-ListCheck.svg');
import Link from 'next/link';
import parse from 'html-react-parser';
import {contentMaxWidth, colors, borderRadius, shadows, spacing, typography, lineHeight, fontWeight, fontSize} from '@/styles/tokens';

export interface IndustryCard {
    src: StaticImageData;
    heading: string;
    body: string;
    body2?: string;
    bullets?: string[];
    redirect?: string;
}

export interface IndustryGridProps {
    heading: string;
    subheading: string;
    contentData: IndustryCard[];
}

const sectionSx = {
    py: {xs: 6, md: 8}
} as const;

const headingSx = {
    ...typography.sectionHeading,
    color: colors.text.primary,
    textAlign: 'center',
    mb: 2,
    maxWidth: contentMaxWidth.standard,
    mx: 'auto'
} as const;

const subtitleSx = {
    ...typography.body,
    color: colors.text.primary,
    textAlign: 'center',
    maxWidth: contentMaxWidth.standard,
    mx: 'auto',
    mb: 6
} as const;

const cardSx = {
    bgcolor: colors.background.default,
    borderRadius: borderRadius.lg,
    border: `1px solid ${colors.neutral.borderFaint}`,
    boxShadow: shadows.awardCardElevated,
    p: spacing[32],
    display: 'flex',
    flexDirection: 'column',
    gap: spacing[24]
} as const;

const iconBgSx = {
    background: `linear-gradient(180deg, ${colors.primary.deep} 0%, ${colors.primary.deeper} 100%)`,
    border: `2px solid ${colors.primary.deep}`,
    borderRadius: borderRadius.xl,
    width: 56,
    height: 56,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
} as const;

const cardTitleSx = {
    ...typography.cardLabel,
    color: colors.text.primary,
    mb: 1
} as const;

const cardBodySx = {
    ...typography.body,
    lineHeight: lineHeight[24],
    color: colors.text.primary
} as const;

const bulletItemSx = {
    display: 'flex',
    alignItems: 'center',
    gap: spacing[16]
} as const;

const bulletTextSx = {
    ...typography.bodySm,
    fontSize: fontSize[16],
    fontWeight: fontWeight.medium,
    color: colors.text.primary
} as const;

const IndustryGrid = ({heading, subheading, contentData}: IndustryGridProps) => {
    return (
        <Box sx={sectionSx}>
            <MUIContainer>
                <Typography variant="h2" component="h2" className="font-headers" sx={headingSx}>
                    {heading}
                </Typography>
                <Typography className="font-regular-text" sx={subtitleSx}>
                    {subheading}
                </Typography>

                <Grid container spacing={4}>
                    {contentData.map((item, index) => (
                        <Grid item xs={12} md={6} key={index}>
                            <Box sx={cardSx}>
                                <Box sx={iconBgSx}>
                                    <DisplayImage
                                        src={item.src}
                                        alt={item.heading}
                                        width={28}
                                        height={28}
                                        style={{filter: 'brightness(0) invert(1)'}}
                                    />
                                </Box>
                                <Box display="flex" flexDirection="column" gap={spacing[8]}>
                                    {item.redirect ? (
                                        <Typography component="h3" sx={cardTitleSx}>
                                            <Link
                                                href={item.redirect}
                                                target="_blank"
                                                className="remove-underline font-headers"
                                                style={{color: colors.text.primary}}
                                            >
                                                {item.heading}
                                            </Link>
                                        </Typography>
                                    ) : (
                                        <Typography component="h3" className="font-headers" sx={cardTitleSx}>
                                            {item.heading}
                                        </Typography>
                                    )}
                                    <Box display="flex" flexDirection="column" gap={spacing[16]}>
                                        <Typography className="font-regular-text remove-underline" sx={cardBodySx}>
                                            {parse(item.body)}
                                        </Typography>
                                        {item.body2 && (
                                            <Typography className="font-regular-text remove-underline" sx={cardBodySx}>
                                                {parse(item.body2)}
                                            </Typography>
                                        )}
                                    </Box>
                                    {item.bullets && (
                                        <Box display="flex" flexDirection="column" gap={spacing[12]} pt={spacing[16]}>
                                            {item.bullets.map((bullet, i) => (
                                                <Box key={i} sx={bulletItemSx}>
                                                    <Box flexShrink={0} width={11.667} height={11.667} position="relative">
                                                        <DisplayImage src={listCheckIcon} alt="Included" fill />
                                                    </Box>
                                                    <Typography className="font-regular-text" sx={bulletTextSx}>
                                                        {bullet}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </MUIContainer>
        </Box>
    );
};

export default memo(IndustryGrid);
